import { FC } from 'react';
import { useLocation } from 'react-router-dom';
import { GroupWrap } from 'src/components/group-wrap';
import { ActiveSection } from 'src/components/layout/header/types';
import { ExternalLink, Link } from 'src/components/navigation/link';
import { useBreakpoints } from 'src/hooks/use-breakpoints';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import { useSystemInfo } from 'src/hooks/use-system-info';

const routes = ['/transport', '/snam-bidding'];
const angularRoutes = [
  '#/auctions',
  '#/fcfs-booking',
  '#/capacity-linking',
  '#/secondary-trading',
  '#/secondary-overview',
  '#/network-point',
  '#/create-proposal',
  '#/edit-proposal',
  '#/create-external-trade',
];

export const isTransportActive = (pathname: string, hash: string) =>
  routes.some(
    (route) =>
      pathname.startsWith(route) ||
      (pathname.startsWith('/platform') &&
        angularRoutes.some((angularRoute) => hash.startsWith(angularRoute)))
  );

export const SecondaryNavTransport: FC<{
  activeSection: ActiveSection;
}> = ({ activeSection }) => {
  const { pathname, hash } = useLocation();
  const monolithUser = useOptionalAuthenticatedMonolithUser();
  const { minTablet } = useBreakpoints();
  const systemInfo = useSystemInfo();

  if (!activeSection.transport) return null;
  if (monolithUser?.isSso) return null;

  return (
    <GroupWrap
      gap={2}
      data-testid="secondary-nav"
      alignItems={!minTablet ? 'stretch' : 'center'}
      flexDirection={!minTablet ? 'column' : 'row'}
    >
      <ExternalLink
        mode="secondary"
        href={`${PRISMA_CONFIG.oldFrontendUrl}/#/auctions`}
        isActive={
          (pathname.startsWith('/platform') && hash.startsWith('#/auctions')) ||
          pathname.startsWith('/transport/auctions')
        }
      >
        Auctions
      </ExternalLink>

      {monolithUser?.isTso ? (
        <>
          <ExternalLink
            mode="secondary"
            href={`${PRISMA_CONFIG.oldFrontendUrl}/#/secondary-overview`}
            isActive={
              (pathname.startsWith('/platform') &&
                hash.startsWith('#/secondary-overview')) ||
              pathname.startsWith('/transport/secondary')
            }
          >
            Secondary Tradings
          </ExternalLink>

          <Link
            mode="secondary"
            to="/transport/network-points"
            isActive={
              (pathname.startsWith('/platform') &&
                hash.startsWith('#/network-point') &&
                !hash.startsWith('#/network-point/bundle')) ||
              pathname.startsWith('/transport/network-points')
            }
          >
            Network Points
          </Link>

          <ExternalLink
            mode="secondary"
            href={`${PRISMA_CONFIG.oldFrontendUrl}/#/network-point/bundle`}
          >
            Bundle Requests
          </ExternalLink>
        </>
      ) : (
        <>
          <ExternalLink
            mode="secondary"
            href={`${PRISMA_CONFIG.oldFrontendUrl}/#/fcfs-booking`}
            isActive={
              (pathname.startsWith('/platform') &&
                hash.startsWith('#/fcfs-booking')) ||
              pathname.startsWith('/transport/fcfs-booking')
            }
          >
            FCFS Booking
          </ExternalLink>

          {systemInfo.featureMultiYearCapacity && (
            <ExternalLink
              mode="secondary"
              href={`${PRISMA_CONFIG.oldFrontendUrl}/#/multi-year-auctions`}
            >
              Multi-Year Capacity
            </ExternalLink>
          )}

          <ExternalLink
            mode="secondary"
            href={`${PRISMA_CONFIG.oldFrontendUrl}/#/secondary-trading`}
            isActive={
              (pathname.startsWith('/platform') &&
                (hash.startsWith('#/secondary-trading') ||
                  hash.startsWith('#/create-external-trade') ||
                  hash.startsWith('#/edit-proposal') ||
                  hash.startsWith('#/create-proposal'))) ||
              pathname.startsWith('/transport/secondary')
            }
          >
            Secondary Trading
          </ExternalLink>

          <Link
            mode="secondary"
            to="/transport/network-points"
            isActive={
              (pathname.startsWith('/platform') &&
                hash.startsWith('#/network-point')) ||
              pathname.startsWith('/transport/network-points')
            }
          >
            Network Points
          </Link>
        </>
      )}
    </GroupWrap>
  );
};
